import React, { useState, useEffect } from 'react';
import { motion, Variants } from 'framer-motion';
import { FaShieldAlt, FaPhoneSlash, FaBullseye, FaRobot, FaDownload, FaUserShield, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

import scam1 from '../../assets/Soluation/scam1.jpg';
import scam2 from '../../assets/Soluation/scam2.webp';
import scam3 from '../../assets/Soluation/scam3.jpg';
import scam4 from '../../assets/Soluation/scam4.jpg';

// Define Framer Motion variants
const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.1
        }
    }
};

const textVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: "easeOut" } }
};

const SolutionPage = () => {
    const solutions = [
        {
            icon: FaBullseye,
            title: "AI-Powered Threat Detection",
            description: "Our advanced AI identifies and flags new and emerging scam patterns in real-time, keeping you protected from the latest threats.",
            color: "text-[#AEEA00]"
        },
        {
            icon: FaPhoneSlash,
            title: "Seamless Auto-Blocking",
            description: "Automatically block a continuously updated list of fraudulent numbers, ensuring your phone stays silent from unwanted calls.",
            color: "text-[#AEEA00]"
        },
        {
            icon: FaUserShield,
            title: "Caller ID for Unknown Numbers",
            description: "See the name and photo of the person calling you, even if they are not in your contacts. Know who's calling before you answer.",
            color: "text-[#AEEA00]"
        },
        {
            icon: FaCheckCircle,
            title: "Community-Driven Database",
            description: "Benefit from a global network of users who report scams, building a robust and reliable database to protect everyone in the community.",
            color: "text-[#AEEA00]"
        }
    ];

    
    const [current, setCurrent] = useState(0);
    const images = [scam1, scam2, scam3, scam4];
  
    useEffect(() => {
      const interval = setInterval(() => {
        setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      }, 3000); // Change image every 3 seconds
      return () => clearInterval(interval);
    }, [images.length]);


    return (
        <div className=" text-gray-800">

             {/* Hero Section */}
            <section className="relative h-[600px] flex items-center bg-gradient-to-br from-blue-700 to-green-600 text-white overflow-hidden">
                {/* Left Image Section */}
                <div className="w-1/2 h-full relative">
                    <img
                        src={images[current]}
                        alt="Scam Preview"
                        className="w-full h-full object-cover transition-opacity duration-1000"
                    />
                    <div className="absolute inset-0 bg-black/50" />
                </div>

                {/* Right Content Section */}
                <div className="w-1/2 relative z-10 px-8 md:px-16 ">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={{
                            hidden: { opacity: 0, x: 50 },
                            visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
                        }}
                    >
                        <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-6 text-[#AEEA00]">
                            Your Solution to Spam & Scams
                        </h1>
                        <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-xl">
                            Tired of fraudulent calls and spam messages? COC provides a robust, all-in-one solution to help you regain control of your phone and peace of mind.
                        </p>
                        <a
                            href="/download"
                            className="inline-block bg-[#AEEA00] text-[#1A237E] py-4 px-10 rounded-full font-bold shadow-lg transition-transform hover:scale-105"
                        >
                            Get Protected Now
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* Problem Statement Section */}
            <section className="py-20 md:py-24 ">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-6">
                        The Problem
                    </h2>
                    <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-12">
                        In today's connected world, spam and scam calls are more than just a nuisance—they are a serious threat to your privacy and financial security. Unidentified callers, fraudulent schemes, and robocalls can lead to significant stress and loss.
                    </p>
                    <motion.div
                        className="grid md:grid-cols-3 gap-10"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={containerVariants}
                    >
                        <motion.div className="flex items-center space-x-4 p-6 bg-red-200 rounded-xl shadow-md" variants={cardVariants}>
                            <FaExclamationTriangle className="text-3xl text-red-500 flex-shrink-0" />
                            <p className="text-gray-700">Financial fraud through phishing and social engineering.</p>
                        </motion.div>
                        <motion.div className="flex items-center space-x-4 p-6 bg-red-200 rounded-xl shadow-md" variants={cardVariants}>
                            <FaExclamationTriangle className="text-3xl text-red-500 flex-shrink-0" />
                            <p className="text-gray-700">Constant interruption from unwanted and anonymous calls.</p>
                        </motion.div>
                        <motion.div className="flex items-center space-x-4 p-6 bg-red-200 rounded-xl shadow-md" variants={cardVariants}>
                            <FaExclamationTriangle className="text-3xl text-red-500 flex-shrink-0" />
                            <p className="text-gray-700">Lack of transparency, leaving you vulnerable to unknown callers.</p>
                        </motion.div>
                    </motion.div>

                   <motion.div
  className="grid md:grid-cols-3 gap-10"
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true, amount: 0.3 }}
  variants={containerVariants}
>
  <motion.div
    className="flex items-center space-x-4 p-6 bg-red-200 rounded-xl shadow-md"
    variants={cardVariants}
  >
    <FaExclamationTriangle className="text-3xl text-red-500 flex-shrink-0" />
    <p className="text-gray-700">
      Scammers pose as family members in distress, demanding urgent money transfers.
    </p>
  </motion.div>

  <motion.div
    className="flex items-center space-x-4 p-6 bg-red-200 rounded-xl shadow-md"
    variants={cardVariants}
  >
    <FaExclamationTriangle className="text-3xl text-red-500 flex-shrink-0" />
    <p className="text-gray-700">
      Fraudsters use fake job offers, lottery wins, or government notices to extract data.
    </p>
  </motion.div>

  <motion.div
    className="flex items-center space-x-4 p-6 bg-red-200 rounded-xl shadow-md"
    variants={cardVariants}
  >
    <FaExclamationTriangle className="text-3xl text-red-500 flex-shrink-0" />
    <p className="text-gray-700">
      Voice phishing and caller ID spoofing make even known numbers untrustworthy.
    </p>
  </motion.div>
</motion.div>
                </div>
            </section>

             <section className="py-16 md:py-20 ">
                <div className="container mx-auto px-6 flex justify-center">
                    <div className="flex flex-col md:flex-row items-center bg-gradient-to-br from-blue-700 to-green-600 rounded-3xl overflow-hidden shadow-2xl w-full md:w-[1280px] h-[360px]">
                        <div className="w-full md:w-1/2 p-6 md:p-10 text-white">
                           <h2 className="text-2xl md:text-3xl font-extrabold leading-tight mb-4 text-[#AEEA00]">
                              Transform Calls into Cash
                           </h2>
                           <p className="text-md md:text-lg mb-6 text-gray-100">
                             COC empowers you to monetize every incoming call. With intelligent tracking, real-time analytics, and secure payouts, we make it easier than ever to earn from genuine call interactions.
                           </p>
                           <button className="bg-[#AEEA00] text-[#1A237E] hover:bg-lime-600 font-semibold px-6 py-2 rounded-full hover:bg-gray-100 transition">
                            Solutions for Every Need
                            </button>
                        </div>
                        <div className="w-full md:w-1/2 h-full">
                            <img
                                src="https://png.pngtree.com/template/20221128/ourlarge/pngtree-scam-alert-poster-template-image_1884083.jpg"
                                alt="iPhone Dashboard"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            
            {/* Our Comprehensive Solution */}
            <section className="py-20 md:py-24 ">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-12">
                        Our Comprehensive Solution
                    </h2>
                    <motion.div
                        className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={containerVariants}
                    >
                        {solutions.map((solution, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-2"
                                variants={cardVariants}
                            >
                                <div className="text-center mb-4">
                                    <solution.icon className={`text-5xl mx-auto ${solution.color}`} />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">{solution.title}</h3>
                                <p className="text-sm text-gray-600">{solution.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

           
            {/* New Section: Our Mission */}
            <section className="py-20 md:py-24 ">
                <div className="container mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-12 text-center">
                        Our Mission
                    </h2>
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <motion.div
                            className="relative flex justify-center items-center order-2 md:order-1"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.5 }}
                            variants={textVariants}
                        >
                           <img
                                src={"https://publicsafety.columbia.edu/sites/default/files/styles/cu_crop/public/content/scam%20page.png?itok=PLvxwjrz"}
                                alt="Mission Image"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </motion.div>
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.5 }}
                            variants={textVariants}
                            className="order-1 md:order-2"
                        >
                            <h3 className="text-2xl font-bold text-[#1A237E] mb-4">
                                Creating a Safer Digital World
                            </h3>
                            <p className="text-lg text-gray-600 mb-4">
                                Our mission is to build a platform that empowers individuals to communicate safely and confidently. By leveraging cutting-edge AI and a collaborative community, we aim to eliminate spam and fraudulent activities, making the digital world a more trustworthy place for everyone.
                            </p>
                            <p className="text-lg text-gray-600">
                                We believe that by working together, we can protect each other from scams and create a connected world where privacy and security are a right, not a privilege.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

             {/* How It Works Section */}
            <section className="py-20 md:py-24 ">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-12">
                        How It Works
                    </h2>
                    <div className="flex flex-col items-center">
                        <div className="relative w-full max-w-3xl">
                            <div className="absolute top-0 bottom-0 left-1/2 -ml-0.5 bg-gray-200 w-1 hidden md:block"></div>
                            
                            <div className="grid md:grid-cols-2 md:gap-x-12">
                                {/* Step 1 */}
                                <motion.div
                                    className="md:text-right relative mb-12 md:mb-0"
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.5 }}
                                    variants={textVariants}
                                >
                                    <div className="flex items-center justify-center md:justify-end mb-4 md:mb-0">
                                        <div className="w-10 h-10 flex items-center justify-center bg-[#1A237E] text-white rounded-full font-bold">1</div>
                                    </div>
                                    <h3 className="text-2xl font-bold text-[#1A237E] mb-2">Download the App</h3>
                                    <p className="text-gray-600">Get the COC app from the Play Store or App Store and install it on your device.</p>
                                </motion.div>

                                {/* Step 2 */}
                                <motion.div
                                    className="md:text-left relative md:mt-12"
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.5 }}
                                    variants={textVariants}
                                >
                                    <div className="flex items-center justify-center md:justify-start mb-4 md:mb-0">
                                        <div className="w-10 h-10 flex items-center justify-center bg-[#1A237E] text-white rounded-full font-bold">2</div>
                                    </div>
                                    <h3 className="text-2xl font-bold text-[#1A237E] mb-2">Activate Protection</h3>
                                    <p className="text-gray-600">Follow the simple setup steps to activate our real-time caller identification and spam-blocking features.</p>
                                </motion.div>

                                {/* Step 3 */}
                                <motion.div
                                    className="md:text-right relative md:mb-12"
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.5 }}
                                    variants={textVariants}
                                >
                                    <div className="flex items-center justify-center md:justify-end mb-4 md:mb-0">
                                        <div className="w-10 h-10 flex items-center justify-center bg-[#1A237E] text-white rounded-full font-bold">3</div>
                                    </div>
                                    <h3 className="text-2xl font-bold text-[#1A237E] mb-2">Get Real-Time Alerts</h3>
                                    <p className="text-gray-600">Our app identifies suspicious callers and displays a clear warning on your screen, so you know not to answer.</p>
                                </motion.div>

                                {/* Step 4 */}
                                <motion.div
                                    className="md:text-left relative"
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.5 }}
                                    variants={textVariants}
                                >
                                    <div className="flex items-center justify-center md:justify-start mb-4 md:mb-0">
                                        <div className="w-10 h-10 flex items-center justify-center bg-[#1A237E] text-white rounded-full font-bold">4</div>
                                    </div>
                                    <h3 className="text-2xl font-bold text-[#1A237E] mb-2">Report & Protect</h3>
                                    <p className="text-gray-600">Help protect the community by reporting scam numbers, making the app smarter and safer for everyone.</p>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            
            
        </div>
    );
};

export default SolutionPage;